import {Card, Descriptions, Tag} from 'antd';
import {useMutation} from '@apollo/client';
import {toast} from 'react-toastify';
import {useNavigate} from 'react-router-dom';
import moment from 'moment';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {
  CREATE_ANNOUNCEMENT,
  UPDATE_ANNOUNCEMENT,
} from 'graphql/mutations/announcement';
import {GET_ALL_ANNOUNCEMENTS} from 'graphql/queries/announcement';
import {extractUserIds} from '../actions';
import styles from './fullScreenPopup.module.scss';

const RENEWAL_PURPOSES = ['DABA_PRO', 'DABA_ACADEMY'];

// Build the createAnnouncement `data` input for a FULL_SCREEN_POPUP from the
// flattened wizard form values.
export const buildFullScreenData = values => {
  const {
    language,
    type,
    purpose,
    openUrlOnClick,
    publishedDate,
    expiredDate,
    report,
    opportunity,
    instrumentId,
    collection,
    // content
    title,
    subTitle,
    headerBadgeText,
    footerText,
    closeButtonLabel,
    actionButtonLabel,
    deeplinkUrl,
    imageUrl,
    announcementIcon,
    popUpContent,
    dismissible,
    maxShowCount,
    benefits,
    subscriptionRenewal,
    translation,
    multilang,
    // target audience
    userSegments,
    riskProfiles,
    countryCodes,
    countriesOfInterest,
    identityCheck,
    documentCheck,
    amlCheck,
    signupStartDate,
    signupEndDate,
    lastAppVisitedStartDate,
    lastAppVisitedEndDate,
    investmentStartDate,
    investmentEndDate,
    directions,
    statuses,
    instrumentKeys,
    audienceInstrumentId,
    stockId,
    ventureAssetId,
    userIds,
  } = values;

  const instrumentIds = [audienceInstrumentId, stockId, ventureAssetId].filter(
    Boolean,
  );
  const needsRenewal = RENEWAL_PURPOSES.includes(purpose);

  // Keep the original index so translated labels (multilang.benefits[i]) stay
  // aligned even when empty rows are filtered out.
  const cleanedBenefits = (benefits || [])
    .map((benefit, originalIndex) => ({
      label: benefit?.label,
      icon: benefit?.icon,
      originalIndex,
    }))
    .filter(benefit => benefit.label || benefit.icon);

  const popupConfig = {
    ...(dismissible !== undefined && {dismissible}),
    ...(maxShowCount !== undefined && maxShowCount !== null && {maxShowCount}),
    ...(purpose && {purpose}),
    ...(needsRenewal &&
      subscriptionRenewal?.planKey &&
      (() => {
        const tabs = (subscriptionRenewal.catalogTabs || []).filter(
          tab => tab && tab.catalogKey,
        );
        // Exactly one tab may be the default — keep only the first flagged one.
        const defaultIndex = tabs.findIndex(tab => tab.isDefault);
        return {
          subscriptionRenewal: {
            planKey: subscriptionRenewal.planKey,
            catalogTabs: tabs.map((tab, index) => {
              const discounts = (tab.discounts || [])
                .filter(
                  discount =>
                    discount &&
                    discount.currency &&
                    discount.value !== undefined &&
                    discount.value !== null &&
                    discount.value !== '',
                )
                .map(discount => ({
                  type: discount.type,
                  value: Number(discount.value),
                  currency: discount.currency,
                }));
              return {
                ...(tab.id && {id: tab.id}),
                catalogKey: tab.catalogKey,
                isDefault: index === defaultIndex,
                appliedDiscount:
                  tab.applyDiscount && discounts.length ? discounts : null,
              };
            }),
          },
        };
      })()),
  };

  return {
    type,
    displayType: 'FULL_SCREEN_POPUP',
    language,
    title,
    subTitle,
    ...(imageUrl && {imageUrl}),
    ...(announcementIcon && {announcementIcon}),
    ...(popUpContent && {popUpContent}),
    ...(headerBadgeText && {headerBadgeText}),
    ...(footerText && {footerText}),
    ...(closeButtonLabel && {closeButtonLabel}),
    ...(actionButtonLabel && {actionButtonLabel}),
    ...(deeplinkUrl && {deeplinkUrl}),
    ...(openUrlOnClick !== undefined && {openUrlOnClick}),
    ...(type === 'SINGLE_REPORT' && report && {report}),
    // Instrument v2. `company` and `instrument` are rejected on dev, and
    // SINGLE_OPPORTUNITY accepts exactly one of `opportunity` (legacy
    // campaign) or `instrumentId` — never both.
    ...(instrumentId
      ? {instrumentId}
      : type === 'SINGLE_OPPORTUNITY' && opportunity
      ? {opportunity}
      : {}),
    ...(type === 'SINGLE_COLLECTION' && collection && {collection}),
    ...(cleanedBenefits.length && {
      benefits: cleanedBenefits.map(benefit => ({
        label: benefit.label,
        icon: benefit.icon,
      })),
    }),
    popupConfig,
    publishedDate: moment(publishedDate).format(),
    expiredDate: moment(expiredDate).format(),
    targetAudience: {
      userSegments,
      riskProfiles,
      countryCodes,
      countriesOfInterest,
      ...(userIds?.length && {userIds: extractUserIds(userIds)}),
      kycStatus: {
        identityCheck,
        documentCheck,
        amlCheck,
      },
      lastAppVisit: {
        ...(lastAppVisitedStartDate && {
          start: moment(lastAppVisitedStartDate).format(),
        }),
        ...(lastAppVisitedEndDate && {
          end: moment(lastAppVisitedEndDate).format(),
        }),
      },
      signUpTime: {
        ...(signupStartDate && {start: moment(signupStartDate).format()}),
        ...(signupEndDate && {end: moment(signupEndDate).format()}),
      },
      transaction: {
        directions,
        statuses,
        ...(investmentStartDate && {
          startDate: moment(investmentStartDate).format(),
        }),
        ...(investmentEndDate && {
          endDate: moment(investmentEndDate).format(),
        }),
        instrumentKeys,
        instrumentIds,
      },
    },
    ...(translation &&
      multilang && {
        multiLang: [
          {
            language: translation,
            title: multilang.title,
            subTitle: multilang.subTitle,
            headerBadgeText: multilang.headerBadgeText,
            footerText: multilang.footerText,
            closeButtonLabel: multilang.closeButtonLabel,
            actionButtonLabel: multilang.actionButtonLabel,
            ...(multilang.popUpContent && {
              popUpContent: multilang.popUpContent,
            }),
            // Translated feature labels, paired with the original icons. Look up
            // the translation by the benefit's original index.
            ...(cleanedBenefits.length && {
              benefits: cleanedBenefits.map(benefit => ({
                label:
                  multilang.benefits?.[benefit.originalIndex]?.label ||
                  benefit.label,
                icon: benefit.icon,
              })),
            }),
          },
        ],
      }),
  };
};

export const StepFullScreenReview = ({form, onBack, editId}) => {
  const navigate = useNavigate();
  const isEdit = !!editId;
  const values = form.getFieldsValue(true);

  const mutationOptions = {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
    refetchQueries: [{query: GET_ALL_ANNOUNCEMENTS}],
  };
  const [createAnnouncement, {loading: creating}] = useMutation(
    CREATE_ANNOUNCEMENT,
    mutationOptions,
  );
  const [updateAnnouncement, {loading: updating}] = useMutation(
    UPDATE_ANNOUNCEMENT,
    mutationOptions,
  );
  const loading = creating || updating;

  const handleSubmit = () => {
    try {
      // Read fresh at click time so any last-minute edits are captured.
      const data = buildFullScreenData(form.getFieldsValue(true));
      const onResult = (result, successMessage) => {
        if (!result || result.__typename === 'Error') {
          toast.error(result?.message || 'Failed to save announcement');
          return;
        }
        toast.success(successMessage);
        navigate('/dashboard/announcements/upcoming');
      };

      if (isEdit) {
        updateAnnouncement({
          variables: {data: {announcementId: editId, ...data}},
        }).then(response =>
          onResult(
            response?.data?.updateAnnouncement,
            'Full screen popup updated successfully!',
          ),
        );
      } else {
        createAnnouncement({variables: {data}}).then(response =>
          onResult(
            response?.data?.createAnnouncement,
            'Full screen popup created successfully!',
          ),
        );
      }
    } catch (error) {
      toast.error('Failed to save full screen popup');
    }
  };

  const needsRenewal = RENEWAL_PURPOSES.includes(values.purpose);
  const benefitsCount = (values.benefits || []).filter(
    benefit => benefit && (benefit.label || benefit.icon),
  ).length;
  const userCount = (values.userIds || []).length;

  return (
    <div className={styles.stepContainer}>
      <h2>Review &amp; {isEdit ? 'Update' : 'Create'}</h2>
      <p>
        Please review the full screen popup details before{' '}
        {isEdit ? 'updating' : 'creating'}.
      </p>

      <Card title="Summary" className={styles.summaryCard}>
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="Language">
            {values.language || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Type">
            {values.type || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Purpose">
            {values.purpose ? <Tag color="blue">{values.purpose}</Tag> : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Title">
            {values.title || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Benefits">
            {benefitsCount}
          </Descriptions.Item>
          <Descriptions.Item label="Targeted Users">
            {userCount ? `${userCount} selected` : 'Filter-based'}
          </Descriptions.Item>
          {needsRenewal ? (
            <Descriptions.Item label="Renewal Plan">
              {values.subscriptionRenewal?.planKey || '—'} (
              {(values.subscriptionRenewal?.catalogTabs || []).length} tab(s))
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item label="Published">
            {values.publishedDate
              ? moment(values.publishedDate).format('DD MMM, YYYY hh:mm a')
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Expires">
            {values.expiredDate
              ? moment(values.expiredDate).format('DD MMM, YYYY hh:mm a')
              : '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <div className={styles.actionButtons}>
        <SubmitButton type="button" handleClick={onBack} label="Back" />
        <SubmitButton
          handleClick={handleSubmit}
          secondary
          label={
            isEdit ? 'Update Full Screen Popup' : 'Create Full Screen Popup'
          }
          disabled={loading}
        />
      </div>
    </div>
  );
};
